import { Button, Flex, Icon, Text, useDisclosure, Tooltip } from "@chakra-ui/react";
import { BaseTable } from "@/components/BaseTable/BaseTable";
import { CellContext, ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { useNavigate, useParams } from "react-router-dom";
import { IVacancyApplication } from "../../../api/vacancyApplications/types";
import { useEffect, useMemo, useState } from "react";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import { formatDate } from "../../../helpers/formatDate";
import { MainModal } from "../../MainModal/MainModal";
import { BaseTableFilters } from "@/components/BaseTableFilters/BaseTableFilters";
import { Pagination } from "../../Pagination/Pagination";
import { fetchAllVacancies } from "../../../store/slices/vacancy";
import { fetchVacancyApplicationById, fetchVacancyApplicationCreate } from "../../../store/slices/vacancyApplications";
import { IVacancy } from "../../../api/vacancy/types";
import { useFormik } from "formik";
import { useSorting } from "../../../hooks/useSorting";
import { FiClock, FiPlus } from "react-icons/fi";
import { Vacancy, VacanciesFilter } from "@/model/vacancy";
import AssignedItemsCell from "@/components/AssignedItemsCell/AssignedItemsCell";
import { VacanciesPopupFilter } from "@/pages/VacanciesPage/components/VacanciesPopupFilter";
import { defaultVacanciesFilterFiedls } from "@/pages/VacanciesPage/_helpers";
import { Customer } from "@/model/customers";
import { Project } from "@/model/projects";
import { Recruiter } from "@/model/recruiters";
import api from "@/api";

const columnHelper = createColumnHelper<IVacancyApplication>();
const columnHelperVacancy = createColumnHelper<Vacancy>();

const VacancyCell = (info: CellContext<IVacancyApplication, string> | undefined) => {
  return (
    <Text fontWeight={500} color="#3b515e">
      {info?.renderValue()}
    </Text>
  );
};

const RecruitersCell = (info: CellContext<Vacancy, Vacancy["recruiters"]>) => {
  const recruiters = info?.row.original.recruiters || [];
  if (!recruiters.length) {
    return <Text>Рекрутеры не назначены</Text>;
  }
  return <AssignedItemsCell items={recruiters.map((r) => r.username)} maxVisible={1} ariaLabel="Назначенные рекрутеры" />;
};

export const CandidateInfo = () => {
  const { currentCandidate, isLoading: isCandidateLoading } = useAppSelector((state: RootState) => state.candidates);
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isOpen: isOpenVacancy, onOpen: onOpenVacancy, onClose: onCloseVacancy } = useDisclosure();
  const { isOpen: isFiltersOpen, onOpen: onFiltersOpen, onClose: onFiltersClose } = useDisclosure();
  const { vacancies, isLoading: isVacancyLoading } = useAppSelector((state) => state.vacancy);
  const { sortColumn, sortDirection, handleSort } = useSorting("vacancy.created_at");
  const [search, setSearch] = useState("");
  const [localSearchValue, setLocalSearchValue] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const [limit, setLimit] = useState(20);
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const [filters, setFilters] = useState<VacanciesFilter>(defaultVacanciesFilterFiedls);
  const [customers, setCustomers] = useState<readonly Customer[]>([]);
  const [allProjects, setAllProjects] = useState<readonly Project[]>([]);
  const [recruiters, setRecruiters] = useState<readonly Recruiter[]>([]);
  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.customer_id) count += 1;
    if (filters.project_id) count += 1;
    if (filters.status) count += 1;
    if (filters.recruiter_id) count += 1;
    if (filters.created_at_from) count += 1;
    if (filters.created_at_to) count += 1;
    return count;
  }, [filters.customer_id, filters.project_id, filters.status, filters.recruiter_id, filters.created_at_from, filters.created_at_to]);
  const formik = useFormik({
    initialValues: {
      candidate_id: Number(id),
      vacancy_id: null,
    },
    onSubmit: () => {},
  });

  useEffect(() => {
    if (id) {
      formik.setFieldValue("candidate_id", Number(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    dispatch(
      fetchAllVacancies({
        params: {
          filters: {
            limit: limit,
            offset: 0,
            ...filters,
            search,
            department_id: departmentId ?? undefined,
            sort_column: sortColumn || undefined,
            sort_direction: sortDirection || undefined,
          },
        },
      }),
    ).then((res: any) => {
      setTotalItems(res.payload.count);
    });
  }, [dispatch, search, sortColumn, sortDirection, limit, departmentId, filters]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const [customersRes, projectsRes, recruitersRes] = await Promise.all([
          api.customers.customers({ params: { filters: { limit: "1000", offset: "0" } } }),
          api.projects.projects({ params: { filters: { limit: "1000", offset: "0" }, expand: ["customer"] } }),
          api.recruiters.recruiters({}),
        ]);
        if (!isMounted) return;
        setCustomers(customersRes?.data?.items || []);
        setAllProjects(projectsRes?.data?.items || []);
        setRecruiters(recruitersRes?.data || []);
      } catch {
        if (!isMounted) return;
        setCustomers([]);
        setAllProjects([]);
        setRecruiters([]);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleRowClick = (row: Row<IVacancy>) => {
    row.toggleSelected();
    if (!row.getIsSelected()) {
      formik.setFieldValue("vacancy_id", row.original.id);
    } else {
      formik.setFieldValue("vacancy_id", null);
    }
  };

  const columnsVacancy = [
    columnHelperVacancy.accessor("customer.name", {
      id: "customer.name",
      header: "Заказчик",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="120px" fontWeight={600} color="#3b515e" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 120,
      minSize: 80,
    }),
    columnHelperVacancy.accessor("name", {
      id: "name",
      header: "Название",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="160px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 160,
      minSize: 120,
    }),
    columnHelperVacancy.accessor("project.name", {
      id: "project.name",
      header: "Проект",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="120px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 120,
      minSize: 80,
    }),
    columnHelperVacancy.accessor("status.name", {
      id: "status.name",
      header: "Cтатус вакансии",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="120px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 120,
      minSize: 80,
    }),
    columnHelperVacancy.accessor("recruiters", {
      id: "recruiters",
      header: "Назначенные рекрутеры",
      cell: RecruitersCell,
    }),
    columnHelperVacancy.accessor("candidates_count", {
      id: "candidates_count",
      header: "Кандидаты",
      cell: (info) => (
        <Tooltip label={info.getValue() || ""} hasArrow>
          <Text noOfLines={1} maxW="80px" fontSize={14}>
            {info.getValue()}
          </Text>
        </Tooltip>
      ),
      enableColumnFilter: true,
      size: 80,
      minSize: 40,
    }),
    columnHelperVacancy.accessor("created_at", {
      id: "created_at",
      header: "Дата создания",
      cell: (info) => (
        <Tooltip label={formatDate(info.getValue() || "")} hasArrow>
          <Text noOfLines={1} maxW="80px" fontSize={14}>
            {formatDate(info.getValue() || "")}
          </Text>
        </Tooltip>
      ),
      enableSorting: true,
      size: 80,
      minSize: 40,
    }),
  ];

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleDepartmentFilterModal = (value: string | undefined) => {
    setDepartmentId(value ? Number(value) : null);
  };

  const handleApplyFilters = (v: VacanciesFilter) => {
    setFilters({ ...filters, ...v });
  };

  const handleResetFilters = () => {
    setFilters(defaultVacanciesFilterFiedls);
  };

  // Функция для фильтрации данных по всем колонкам таблицы
  const getFilteredVacancyApplications = useMemo(() => {
    if (!localSearchValue.trim()) {
      return currentCandidate?.vacancy_applications ?? [];
    }

    const searchLower = localSearchValue.toLowerCase();

    return (currentCandidate?.vacancy_applications ?? []).filter((application) => {
      // Поиск по названию вакансии
      const vacancyName = application.vacancy?.name?.toLowerCase() || "";

      // Поиск по этапу/статусу
      const statusName = application.status?.name?.toLowerCase() || "";

      // Поиск по количеству дней в статусе
      const statusDays = application.status?.days?.toString() || "";

      // Поиск по ответственному рекрутеру
      const recruiterName = application.created_by?.username?.toLowerCase() || "";

      // Поиск по дате собеседования/ТЗ
      const interviewDate = application.interview_at ? formatDate(application.interview_at).toLowerCase() : "";

      // Поиск по дате прикрепления кандидата
      const createdDate = formatDate(application.created_at).toLowerCase();

      return (
        vacancyName.includes(searchLower) ||
        statusName.includes(searchLower) ||
        statusDays.includes(searchLower) ||
        recruiterName.includes(searchLower) ||
        interviewDate.includes(searchLower) ||
        createdDate.includes(searchLower)
      );
    });
  }, [currentCandidate?.vacancy_applications, localSearchValue]);

  const handleLocalSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearchValue(e.target.value);
  };

  const columns = useMemo<Array<ColumnDef<IVacancyApplication, any>>>(
    () => [
      columnHelper.accessor("vacancy.name", {
        header: "Вакансия",
        cell: VacancyCell,
      }),
      columnHelper.accessor("status.name", {
        header: () => (
          <Text w="100%" textAlign="center">
            Этап
          </Text>
        ),
        cell: (info) => (
          <Text textAlign="center" fontWeight={500} color="#3b515e" padding="2px 8px" borderRadius="9999px" border="1px solid #e1e7ef">
            {info.getValue()}
          </Text>
        ),
      }),
      columnHelper.accessor("status.days", {
        header: "Дней в статусе",
        cell: (info) => (
          <Flex alignItems="center" gap="4px">
            <Icon as={FiClock as any} boxSize={4} />
            <Text>{info.getValue()}</Text>
          </Flex>
        ),
      }),
      columnHelper.accessor("created_by.username", {
        header: "Ответсвенный рекрутер",
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("interview_at", {
        header: "Дата собеседования / ТЗ",
        cell: (info) => {
          return <Text>{info.getValue() ? formatDate(info.getValue()) : ""}</Text>;
        },
      }),
      columnHelper.accessor("created_at", {
        header: "Дата прикрепления кандидата",
        cell: (info) => {
          return <Text>{formatDate(info.getValue())}</Text>;
        },
        enableSorting: true,
      }),
    ],
    [],
  );
  return (
    <Flex flexDirection="column" bgColor="white" borderColor="#e1e7ef" borderWidth="1px" borderRadius="8px" padding="16px">
      <Flex gap="16px" alignItems="center" marginBottom="16px" justifyContent="space-between">
        <Text fontWeight={700} fontSize={24} lineHeight="32px" color="#3b515e">
          Вакансии кандидата
        </Text>
        <Button leftIcon={<Icon as={FiPlus as any} boxSize={4} />} variant="solid" bgColor="#61a6fa" color="white" onClick={onOpenVacancy}>
          Добавить к вакансии
        </Button>
      </Flex>

      <BaseTableFilters
        handleSearch={(value) => setLocalSearchValue(value)}
        placeHolderInput="Поиск по таблице..."
        searchValue={localSearchValue}
        isShowBtnPopupFilters={false}
        isLoading={isCandidateLoading}
        isShowCreateButton={false}
      />

      <BaseTable
        list={getFilteredVacancyApplications}
        columns={columns as Array<ColumnDef<IVacancyApplication, any>>}
        sortHandler={({ id, direction }) => handleSort({ id, direction: direction ?? null })}
        sortState={{
          column: sortColumn ?? "",
          direction: sortDirection,
        }}
        isLoading={isCandidateLoading}
        clickHandler={(id) => {
          navigate(`/vacancy/${id}/annex`);
        }}
      />
      <MainModal width={"80vw"} showFooter={false} isOpen={isOpenVacancy} onClose={onCloseVacancy} title={"Добавить кандидата"}>
        <Flex flexDirection="column" gap="16px">
          <BaseTableFilters
            handleSearch={handleSearch}
            placeHolderInput="Поиск по вакансиям"
            searchValue={search}
            isShowBtnPopupFilters
            selectedFiltersCount={appliedFiltersCount}
            onClickBtnPopupFilters={onFiltersOpen}
            isOpenPopupFilters={isFiltersOpen}
            onClosePopupFilters={onFiltersClose}
            popupFilters={
              <VacanciesPopupFilter
                values={filters}
                onApply={(v) => {
                  handleApplyFilters(v);
                  onFiltersClose();
                }}
                onReset={() => {
                  handleResetFilters();
                  onFiltersClose();
                }}
                customers={customers}
                allProjects={allProjects}
                recruiters={recruiters}
              />
            }
            isLoading={isVacancyLoading}
            isShowCreateButton={false}
          />
          <BaseTable
            list={(vacancies?.items ?? []) as unknown as IVacancy[]}
            columns={columnsVacancy as Array<ColumnDef<IVacancy, any>>}
            rowClick={handleRowClick}
            canSelect={true}
            minHeight="0px"
            maxHeight="50vh"
            sortHandler={({ id, direction }) => handleSort({ id, direction: direction ?? null })}
            sortState={{
              column: sortColumn ?? "",
              direction: sortDirection,
            }}
            isLoading={isVacancyLoading}
          />
          <Pagination
            isLoading={isVacancyLoading}
            totalItems={totalItems}
            useUrlParams={false}
            defaultPage={1}
            defaultLimit={limit}
            onPageChange={(offset, limit) => {
              setLimit(limit);
              dispatch(
                fetchAllVacancies({
                  params: {
                    filters: {
                      limit,
                      offset,
                      ...filters,
                      search,
                      department_id: departmentId ?? undefined,
                      sort_column: sortColumn || undefined,
                      sort_direction: sortDirection || undefined,
                    },
                  },
                }),
              );
            }}
          />
          <Button
            isDisabled={formik.values.vacancy_id === null}
            onClick={() => {
              dispatch(
                fetchVacancyApplicationCreate({
                  candidate_id: formik.values.candidate_id,
                  vacancy_id: formik.values.vacancy_id ?? 0,
                }),
              ).then((res: any) => {
                if (res.payload.id) {
                  dispatch(fetchVacancyApplicationById(res.payload.id)).finally(() => {
                    navigate(`/vacancy/${res.payload.id}/annex`);
                  });
                }
              });
            }}
          >
            Добавить
          </Button>
        </Flex>
      </MainModal>
    </Flex>
  );
};
